import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

/** The two presentations a record/plan collection can be browsed in. */
export type ViewMode = "grid" | "list";

export type ViewModeContextValue = [ViewMode, (mode: ViewMode) => void];

function isViewMode(value: string | null): value is ViewMode {
  return value === "grid" || value === "list";
}

interface CreateViewModeOptions {
  /** localStorage key the choice is persisted under, e.g. `otus:plans-view-mode`. */
  storageKey: string;
  /** Provider name, used verbatim in the "used outside a provider" error. */
  providerName: string;
  /** Hook name, used verbatim in the same error. */
  hookName: string;
  /** Mode used when nothing valid is stored. Defaults to `"grid"`. */
  defaultMode?: ViewMode;
}

/**
 * Builds a grid/list view-mode context: a provider that owns the choice and a
 * hook that reads it as a `[viewMode, setViewMode]` tuple.
 *
 * Two pages need exactly this — the plans page ([[plans-view.spec]]) and the
 * photos page ([[records-view.spec]]) — and each keeps its own independent
 * preference under its own storage key. The behavior around that choice is
 * identical for both, so it lives here once: the provider sits at the project
 * layout so the toggle in [[project-header.spec]] and the content area below it
 * share one source of truth, and the choice is persisted **globally per
 * browser** (not per project).
 *
 * Reads are SSR-safe (no `window` during a server render) and tolerate a
 * throwing or unavailable `localStorage` (private mode, blocked storage) — any
 * failure falls back to the default mode rather than crashing the render.
 * Writes are best-effort: a blocked or full quota keeps the in-memory choice
 * for the session without breaking the toggle.
 */
export function createViewMode({
  storageKey,
  providerName,
  hookName,
  defaultMode = "grid",
}: CreateViewModeOptions) {
  function readStored(): ViewMode {
    if (typeof window === "undefined") return defaultMode;
    try {
      const raw = window.localStorage.getItem(storageKey);
      return isViewMode(raw) ? raw : defaultMode;
    } catch {
      return defaultMode;
    }
  }

  const Context = createContext<ViewModeContextValue | null>(null);

  function Provider({ children }: { children: ReactNode }) {
    const [viewMode, setViewModeState] = useState<ViewMode>(() => readStored());

    const setViewMode = useCallback((mode: ViewMode) => {
      setViewModeState(mode);
      try {
        window.localStorage.setItem(storageKey, mode);
      } catch {
        // Storage unavailable (private mode / quota / blocked) — keep the
        // in-memory choice for this session; persistence is best-effort.
      }
    }, []);

    const value = useMemo<ViewModeContextValue>(
      () => [viewMode, setViewMode],
      [viewMode, setViewMode],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  }
  Provider.displayName = providerName;

  function useViewMode(): ViewModeContextValue {
    const ctx = useContext(Context);
    if (!ctx) {
      throw new Error(`${hookName} must be used within a ${providerName}`);
    }
    return ctx;
  }

  return { Provider, useViewMode };
}
